import { useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import UserContext from "context/UserContext";
import { ROUTES } from "constants/routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomers } from "hooks/useGestionCustomers";

export function competitionCustomerId(competition, tournament) {
  const value =
    competition?.customer_id ??
    competition?.data?.customer_id ??
    tournament?.competition?.data?.customer_id ??
    tournament?.competition?.customer_id ??
    null;

  return value == null || value === "" ? null : value;
}

/**
 * Resource-scoped pages (public /torneo, planillero) must pass resourceCustomerId so a
 * persisted gestión selection does not leak chrome onto unrelated tournaments.
 * Omit resourceCustomerId for unscoped chrome (user menu).
 */
export function resolveHasGestionContext({
  hasGestionAccess,
  selectedCustomerId,
  resourceCustomerId,
} = {}) {
  if (!hasGestionAccess || selectedCustomerId == null || selectedCustomerId === "") {
    return false;
  }
  if (resourceCustomerId === undefined) {
    return true;
  }
  if (resourceCustomerId == null || resourceCustomerId === "") {
    return false;
  }
  return String(resourceCustomerId) === String(selectedCustomerId);
}

/**
 * Resolves gestión customer context for cross-shell navigation (gestión ↔ competition ↔ tournament).
 * Uses location.state when present, otherwise the persisted selectedGestionCustomer.
 */
export function useGestionNavigation({ resourceCustomerId } = {}) {
  const location = useLocation() ?? { pathname: "", state: null };
  const { selectedGestionCustomer } = useContext(UserContext);
  const { hasGestionAccess, gestionCustomers } = useGestionCustomers();

  const customerId = location.state?.gestionCustomerId || selectedGestionCustomer?.id;

  const customer = useMemo(() => {
    if (!customerId) return selectedGestionCustomer;
    return (
      gestionCustomers.find((item) => String(item.id) === String(customerId)) ||
      (String(selectedGestionCustomer?.id) === String(customerId) ? selectedGestionCustomer : null)
    );
  }, [customerId, gestionCustomers, selectedGestionCustomer]);

  const gestionHomePath = customerId ? GESTION_ROUTES.dashboard(String(customerId)) : ROUTES.ADMIN.GESTION;
  const gestionCompetitionsPath = customerId ? GESTION_ROUTES.tournaments(String(customerId)) : null;
  const gestionMatchesPath = customerId ? GESTION_ROUTES.matches(String(customerId)) : null;

  const contextualReturnPath = useMemo(() => {
    if (location.state?.gestionReturnPath) {
      return location.state.gestionReturnPath;
    }
    if (!customerId) {
      return ROUTES.ADMIN.GESTION;
    }
    if (location.pathname.includes("/planillero/")) {
      return gestionMatchesPath || gestionHomePath;
    }
    if (location.pathname.startsWith(`${ROUTES.PUBLIC.TOURNAMENT}/`)) {
      return gestionCompetitionsPath || gestionHomePath;
    }
    if (location.pathname.startsWith(`${ROUTES.ADMIN.GESTION}/`) && location.pathname.includes("/tournaments")) {
      return gestionCompetitionsPath || gestionHomePath;
    }
    if (location.pathname.startsWith(`${ROUTES.ADMIN.GESTION}/`) && location.pathname.includes("/competitions/")) {
      return gestionCompetitionsPath || gestionHomePath;
    }
    if (location.pathname.startsWith(`${ROUTES.ADMIN.EVENT}/`)) {
      return gestionCompetitionsPath || gestionHomePath;
    }
    return gestionHomePath;
  }, [
    customerId,
    gestionCompetitionsPath,
    gestionHomePath,
    gestionMatchesPath,
    location.pathname,
    location.state?.gestionReturnPath,
  ]);

  return {
    hasGestionAccess,
    hasGestionContext: resolveHasGestionContext({
      hasGestionAccess,
      selectedCustomerId: customerId,
      resourceCustomerId,
    }),
    customerId: customerId ? String(customerId) : null,
    customerName: customer?.name ?? selectedGestionCustomer?.name ?? "",
    returnPath: contextualReturnPath,
    gestionHomePath,
    gestionCompetitionsPath,
    gestionMatchesPath,
  };
}
